// src/sectors/pilates/sessions/components/calendar/MobileDayList/parts/SlotPickerDialog.jsx
import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { hm } from '../mobileDayList.utils';

/* ---------------- small atoms ---------------- */
function toneColor(theme, tone) {
  if (tone === 'danger') return theme.palette.error.main;
  if (tone === 'success') return theme.palette.success.main;
  return theme.palette.info.main;
}

function SectionTitle({ icon, title, subtitle }) {
  return (
    <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
      <MDBox
        sx={(t) => ({
          width: 36,
          height: 36,
          borderRadius: '14px',
          display: 'grid',
          placeItems: 'center',
          border: `1px solid ${alpha(
            t.palette.text.primary,
            t.palette.mode === 'dark' ? 0.16 : 0.12
          )}`,
          background:
            t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
          flex: '0 0 auto',
        })}
      >
        <Icon sx={{ fontSize: 18, opacity: 0.9 }}>{icon}</Icon>
      </MDBox>

      <MDBox sx={{ minWidth: 0 }}>
        <MDTypography variant="button" sx={{ fontWeight: 1000, lineHeight: 1.1 }}>
          {title}
        </MDTypography>
        {subtitle ? (
          <MDTypography variant="caption" sx={{ opacity: 0.75, fontWeight: 800 }} noWrap>
            {subtitle}
          </MDTypography>
        ) : null}
      </MDBox>
    </MDBox>
  );
}

function SoftChoiceChip({ label, selected, disabled, onClick, tone = 'info', icon }) {
  return (
    <Chip
      label={label}
      icon={icon ? <Icon sx={{ fontSize: 18 }}>{icon}</Icon> : undefined}
      clickable={!disabled}
      onClick={disabled ? undefined : onClick}
      variant={selected ? 'filled' : 'outlined'}
      sx={(t) => {
        const isDark = t.palette.mode === 'dark';
        const c = toneColor(t, tone);

        const border = `1px solid ${alpha(t.palette.text.primary, isDark ? 0.16 : 0.12)}`;
        const bg = selected
          ? alpha(c, isDark ? 0.22 : 0.16)
          : isDark
          ? 'rgba(255,255,255,0.03)'
          : 'rgba(255,255,255,0.88)';

        return {
          borderRadius: '999px',
          border,
          background: bg,
          fontWeight: 950,
          px: 0.4,
          '& .MuiChip-label': { px: 0.7 },
          opacity: disabled ? 0.55 : 1,
          transition: 'transform .14s ease, box-shadow .14s ease, background .14s ease',
          boxShadow: selected ? `0 14px 28px ${alpha(c, isDark ? 0.16 : 0.12)}` : 'none',
          '&:hover': disabled
            ? {}
            : {
                transform: 'translateY(-1px)',
                boxShadow: `0 18px 34px ${alpha(c, isDark ? 0.18 : 0.14)}`,
              },
          '&:active': disabled ? {} : { transform: 'scale(0.996)' },
        };
      }}
    />
  );
}

export default function SlotPickerDialog({
  open,
  onClose,
  kind,
  days,
  slots,
  pickDay,
  pickStartMin,
  setPickDay,
  setPickStartMin,
  busy,
  errMsg,
  onConfirm,
}) {
  const theme = useMuiTheme();

  const title =
    kind === 'CREATE' ? 'Seans ekle' : kind === 'MOVE' ? 'Seansı taşı' : 'Seansı kopyala';

  const headerIcon =
    kind === 'CREATE' ? 'add_circle' : kind === 'MOVE' ? 'open_with' : 'content_copy';

  const canConfirm = Boolean(pickDay) && pickStartMin != null && !busy;

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: '22px',
          overflow: 'hidden',
          border: `1px solid ${alpha(
            theme.palette.text.primary,
            theme.palette.mode === 'dark' ? 0.16 : 0.12
          )}`,
          background: theme.palette.mode === 'dark' ? alpha('#0b1220', 0.92) : alpha('#fff', 0.96),
          backdropFilter: 'blur(10px)',
          boxShadow: `0 26px 70px ${alpha('#000', theme.palette.mode === 'dark' ? 0.55 : 0.18)}`,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1.1 }}>
        <MDBox
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: 1 }}
        >
          <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <MDBox
              sx={{
                width: 42,
                height: 42,
                borderRadius: '16px',
                display: 'grid',
                placeItems: 'center',
                border: `1px solid ${alpha(theme.palette.info.main, 0.22)}`,
                background: alpha(
                  theme.palette.info.main,
                  theme.palette.mode === 'dark' ? 0.16 : 0.12
                ),
                flex: '0 0 auto',
              }}
            >
              <Icon sx={{ opacity: 0.9 }}>{headerIcon}</Icon>
            </MDBox>

            <MDBox sx={{ minWidth: 0 }}>
              <MDTypography component="div" variant="h6" sx={{ fontWeight: 1100, lineHeight: 1.1 }}>
                {title}
              </MDTypography>
              <MDTypography
                component="div"
                variant="caption"
                sx={{ opacity: 0.75, fontWeight: 900 }}
                noWrap
              >
                Gün ve saat seç
              </MDTypography>
            </MDBox>
          </MDBox>

          <IconButton
            size="small"
            onClick={onClose}
            disabled={busy}
            sx={{
              borderRadius: '14px',
              border: `1px solid ${alpha(
                theme.palette.text.primary,
                theme.palette.mode === 'dark' ? 0.16 : 0.12
              )}`,
              background:
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.85)',
            }}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 1.35, display: 'grid', gap: 1.15 }}>
        {errMsg ? (
          <MDBox
            sx={{
              px: 1.1,
              py: 0.9,
              borderRadius: '16px',
              border: `1px solid ${alpha(theme.palette.error.main, 0.35)}`,
              background: alpha(theme.palette.error.main, 0.08),
              display: 'flex',
              alignItems: 'center',
              gap: 0.8,
            }}
          >
            <Icon sx={{ fontSize: 18, color: theme.palette.error.main }}>error_outline</Icon>
            <MDTypography
              component="div"
              variant="caption"
              fontWeight="bold"
              sx={{ color: theme.palette.error.main }}
            >
              {errMsg}
            </MDTypography>
          </MDBox>
        ) : null}

        <MDBox sx={{ display: 'grid', gap: 0.8 }}>
          <SectionTitle
            icon="event"
            title="Gün"
            subtitle={pickDay ? `Seçilen: ${pickDay}` : 'Bir gün seç'}
          />

          <MDBox
            sx={{
              display: 'flex',
              gap: 0.8,
              overflowX: 'auto',
              pb: 0.4,
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {(days || []).map((d) => (
              <SoftChoiceChip
                key={d}
                label={String(d).slice(5, 10)} // "02-11"
                selected={pickDay === d}
                disabled={busy}
                onClick={() => setPickDay(d)}
                tone="info"
                icon={pickDay === d ? 'check' : undefined}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox sx={{ display: 'grid', gap: 0.8 }}>
          <SectionTitle
            icon="schedule"
            title="Saat"
            subtitle={pickStartMin != null ? `Seçilen: ${hm(pickStartMin)}` : 'Bir saat seç'}
          />

          <MDBox
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', // mobil için 4 sütun çok iyi
              gap: 0.75,
              maxHeight: 220, // ✅ çok slot varsa dialog uzamasın
              overflowY: 'auto',
              pr: 0.25,
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {(slots || []).map((m) => {
              const selected = Number(pickStartMin) === Number(m);
              return (
                <SoftChoiceChip
                  key={m}
                  label={hm(m)}
                  selected={selected}
                  disabled={busy}
                  onClick={() => setPickStartMin(Number(m))}
                  tone={selected ? 'success' : 'info'}
                  icon={selected ? 'done' : undefined}
                />
              );
            })}
          </MDBox>
        </MDBox>

        <MDBox
          sx={{
            mt: 0.25,
            borderRadius: '16px',
            border: `1px dashed ${alpha(
              theme.palette.text.primary,
              theme.palette.mode === 'dark' ? 0.18 : 0.16
            )}`,
            background:
              theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : alpha('#0b1220', 0.03),
            p: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 0.8,
          }}
        >
          <Icon sx={{ fontSize: 18, opacity: 0.8 }}>touch_app</Icon>
          <MDTypography variant="caption" sx={{ fontWeight: 900, opacity: 0.82 }}>
            Seçim yaptıktan sonra <b>Onayla</b> ile devam edeceksin.
          </MDTypography>
        </MDBox>
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 1.6, gap: 1 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, flex: 1 }}
          disabled={busy}
        >
          Vazgeç
        </MDButton>

        <MDButton
          variant="gradient"
          color="info"
          disabled={!canConfirm}
          onClick={onConfirm}
          sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 1000, flex: 1 }}
        >
          {busy ? 'İşleniyor...' : 'Onayla'}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
